import { observer, Observer } from 'mobx-react-lite'
import MeasurementBasedCare from './MeasurementBasedCare'
import BasicInfo from 'components/Dashboard/cards/common/BasicInfo'
import HorizontalRowsInfo from 'components/Dashboard/cards/common/HorizontalRowsInfo'
import { getPhone, getFullAddress, getHeight, getWeight } from 'util/apiFieldFormatters'
import { formatDate } from 'util/formatters'
import capitalize from 'lodash/capitalize'

import PlusIcon from '@ui-components-3/icons/lib/solid/Plus'
import Modals from 'singletons/Modals'
import { getUnit } from 'util/apiFieldFormatters'
import MedicalChartContent from './MedicalChartContent'
import NotesBlock from './NotesBlock'
import PluralNotesBlock from './PluralNotesBlock'
import CarePlan from './CarePlan'
import DischargeInstructions from './DischargeInstructions'
import Restrictions from './Restrictions'
import Referrals from './Referrals'
import ImagesContent from 'components/Dashboard/Main/Visit/TabPanel/tabs/common/ImagesContent'
import addDotToICDCode from 'util/addDotToICDCode'

import Section from 'components/Dashboard/cards/RxChange/components/Section'

import useStyles from './styles'

type CollapsedBlockProps = {
  // TODO - improve type
  visit: any
  parentVisit: any
  editable?: boolean
  memberMode?: boolean
  providerMode?: boolean
}

const CollapsedBlock = ({ visit, parentVisit, editable, memberMode, providerMode }: CollapsedBlockProps) => {
  const classes = useStyles()
  const isSuccess = visit?.isSuccess

  return (
    <div className={classes.collapsedContainer}>
      <Observer>
        {() =>
          visit.dxs?.map(({ id, code, description }) => (
            <p key={id} className="typography-body-l text-neutral-800 -mt-2 mb-2 ml-6">
              {addDotToICDCode(code)} {description}
            </p>
          )) || null
        }
      </Observer>
      {memberMode || providerMode ? (
        <Observer>
          {() => {
            const { memberExtendedData } = visit

            if (!memberExtendedData) {
              return null
            }
            const { addresses, phones, dob, degreeClassification, pronoun, graduationSemester, graduationYear } =
              memberExtendedData.privateData.member
            const { heightFt, heightIn, weightLbs } = memberExtendedData.privateData.medicalHistory

            // TODO - improve type
            // @ts-ignore
            const { street, state } = getFullAddress(addresses, 'extended')
            const patientPhone = getPhone(phones)

            const emptyValue = 'Not Answered'

            const infoRows = !isSuccess
              ? {
                  gender: visit.genderIdentity,
                  height: getHeight(heightFt, heightIn),
                  weight: getWeight(weightLbs),
                }
              : {
                  degreeClassification: degreeClassification ? capitalize(degreeClassification) : emptyValue,
                  graduationDate:
                    graduationYear && graduationSemester ? `${graduationYear} ${graduationSemester}` : emptyValue,
                  pronoun: pronoun || emptyValue,
                }

            return (
              <>
                <BasicInfo address={street} state={state} phone={patientPhone} />
                <div className={classes.horizontalRowsContainer}>
                  <HorizontalRowsInfo {...infoRows} dob={formatDate(dob)} />
                </div>
                <Observer>
                  {() =>
                    visit.intakes ? (
                      <>
                        <hr className="my-8" />
                        <MeasurementBasedCare visit={visit} />
                      </>
                    ) : null
                  }
                </Observer>
                {visit.isPrescribable && !providerMode ? (
                  <>
                    <hr className="my-8" />
                    <Section
                      sectionName={'Prescriber'}
                      title={visit.providerName}
                      address={visit.provider?.address.street}
                      state={visit.provider?.address.state}
                      phone={visit.provider?.phone}
                      className="pt-2"
                    />
                  </>
                ) : null}
              </>
            )
          }}
        </Observer>
      ) : null}
      <Observer>
        {() =>
          visit.images.list.length ? (
            <>
              <hr className="my-8" />
              <p className="typography-caption text-neutral-500">Photos</p>
              <div className={classes.photos}>
                <ImagesContent visit={visit} />
              </div>
            </>
          ) : null
        }
      </Observer>
      <Observer>
        {() =>
          visit.assessmentCompleted ? (
            <>
              <hr className="my-8" />
              <p className="typography-caption text-neutral-500">Intake Form</p>
              <button
                className="btn btn-neutral mt-4 w-max"
                type="button"
                onClick={() => Modals.open('intakeResults', { visit })}
              >
                Show Intake Form
              </button>
            </>
          ) : null
        }
      </Observer>
      <Observer>
        {() =>
          (visit.isPrescribable && visit.rxs.list.length) || (editable && visit.canAddPrescription) ? (
            <hr className="my-8" />
          ) : null
        }
      </Observer>
      <Observer>
        {() =>
          visit.isPrescribable && visit.rxs.list.length ? (
            <>
              {visit.rxs.list.map((rx) => (
                <MedicalChartContent
                  visit={visit}
                  key={rx.id}
                  prescriptionId={rx.prescriptionId}
                  pharmacy={rx.pharmacy}
                  name={rx.logName}
                  quantity={rx.quantity}
                  refills={rx.refills}
                  daysSupply={rx.daysSupply}
                  directions={rx.directions}
                  unit={getUnit(rx)}
                  notes={rx.pharmacyNotes}
                  substitutions={rx.substitutions}
                  writtenDate={rx.writtenDate}
                  isChangeButtonShown={editable}
                  removalForbidden={rx.removalForbidden}
                  cancellationReason={rx.cancellationReason}
                />
              ))}
            </>
          ) : null
        }
      </Observer>
      <Observer>
        {() =>
          editable && visit.canAddPrescription ? (
            <button
              className="btn btn-neutral mt-4 w-max"
              type="button"
              onClick={() => visit.openAddPrescriptionModal()}
              disabled={visit.isMemberLocked}
            >
              Add Prescription
            </button>
          ) : null
        }
      </Observer>
      <Observer>
        {() =>
          visit?.safetyPlan?.form ? (
            <>
              <hr className="my-8" />
              <p className="typography-caption text-neutral-500">Safety Plan</p>
              <button
                className="btn btn-neutral mt-4 w-max"
                type="button"
                onClick={() => Modals.open('safetyPlan', { visit })}
              >
                View Safety Plan
              </button>
            </>
          ) : null
        }
      </Observer>
      {visit.followupInstructions || visit.dischargeInstructions.list?.length || visit.restriction.list?.length ? (
        <hr className="my-8" />
      ) : null}

      {visit.followupInstructions ? (
        <CarePlan instructions={visit.followupInstructions} title={`${isSuccess ? 'Success' : 'Care'} plan`} />
      ) : null}
      {visit.dischargeInstructions.list?.length ? <DischargeInstructions visit={visit} isSuccess={isSuccess} /> : null}
      {visit.restriction.list?.length ? <Restrictions list={visit.restriction.list} /> : null}
      <NotesBlock type={'history'} visit={visit} />
      <NotesBlock type={'exam'} visit={visit} />
      <Observer>
        {() =>
          visit.chat.length ? (
            <button
              type="button"
              className="btn btn-neutral w-max"
              onClick={() => Modals.open('chatHistory', { visit })}
            >
              Chat History
            </button>
          ) : null
        }
      </Observer>

      <Observer>
        {() =>
          visit.canEditPsychotherapyNotes && visit.notes?.psychotherapy ? (
            <>
              <hr className="my-8" />
              <NotesBlock type={'psychotherapy'} visit={visit} />
              <div>
                <button
                  className="btn btn-neutral mt-4"
                  type="button"
                  onClick={() => Modals.open('psychotherapyNotes', { visit })}
                  disabled={visit.isMemberLocked}
                >
                  Edit Private Notes
                </button>
              </div>
            </>
          ) : null
        }
      </Observer>
      {visit.referral.empty ? null : (
        <>
          <hr className="my-8" />
          <Referrals visit={visit} />
        </>
      )}
      <hr className="my-8" />

      <Observer>
        {() =>
          visit.canEditPsychotherapyNotes && !visit.notes?.psychotherapy ? (
            <button
              className="btn btn-neutral mt-4 mb-7 w-max"
              type="button"
              onClick={() => Modals.open('psychotherapyNotes', { visit })}
              disabled={visit.isMemberLocked}
            >
              <PlusIcon className="fill-white h-3 w-3" aria-hidden="true" /> Private Notes
            </button>
          ) : null
        }
      </Observer>

      <PluralNotesBlock type={'addendum'} visit={visit} />
      <button
        className="btn btn-neutral w-max"
        type="button"
        onClick={() => Modals.open('addAddendum', { visit, parentVisit })}
        disabled={visit.isMemberLocked}
      >
        Add Addendum
      </button>
    </div>
  )
}

export default observer(CollapsedBlock)
